import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import {
  exportAuditLogsCsv,
  exportAuditLogsPdf,
  listAuditActivityTypes,
  listAuditUsers,
  listAuditLogs,
  type AuditLogEntry,
  type AuditLogUser,
} from '../../services/auditLogs';
import FormattedDateInput from '../../components/ui/FormattedDateInput';
import { formatAuditDateTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';

function formatActivityLabel(type: string): string {
  return type.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

const PILL_VARIANT: Record<string, string> = {
  USER_CREATED: 'user-l', USER_UPDATED: 'user', USER_DELETED: 'user-d',
  TASK_STARTED: 'task-l', TASK_ASSIGNED: 'task-l', SUBSIDIARY_TASK_STARTED: 'task-l',
  TASK_FORWARDED: 'task', TASK_UNASSIGNED: 'task',
  TASK_COMPLETED: 'task-d', SUBSIDIARY_TASK_COMPLETED: 'task-d', TASK_REVERTED: 'task-d', TASK_REJECTED: 'task-d',
  PHYSICAL_FILE_RETRIEVAL_REQUESTED: 'file-l',
  FILE_CHECK_IN: 'file', PHYSICAL_FILE_CHECKED_IN: 'file',
  FILE_CHECK_OUT: 'file-d', PHYSICAL_FILE_CHECKED_OUT: 'file-d', FILE_LOCATION_UPDATED: 'file-d',
  DOCUMENT_UPLOADED: 'doc-l', DOCUMENT_DELETED: 'doc-d',
  COMMUNICATION_ADDED: 'comms', MINUTES_ADDED: 'comms-d',
  CREATED: 'workflow-l', UPDATED: 'workflow', ACTIVATED: 'workflow-d',
  FAILED_LOGIN: 'warn',
  DRAFT_SAVED: 'case', SUBMITTED: 'case', DUPLICATED: 'case', ASSIGNED: 'case', DELETED: 'case-d',
};

function ActivityTypePill({ type }: { type: string }) {
  const variant = PILL_VARIANT[type.toUpperCase()] ?? 'case';
  return <span className={`al-pill al-pill-${variant}`}>{formatActivityLabel(type)}</span>;
}

function MultiSelect({
  label,
  value,
  options,
  onChange,
  placeholder,
  getLabel,
}: {
  label: string;
  value: string[];
  options: string[];
  placeholder: string;
  onChange: (v: string[]) => void;
  getLabel?: (opt: string) => string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest?.('.ms-wrap')) setOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [open]);

  const text = value.length === 0 ? placeholder : t('admin.shared.selectedCount', { count: value.length });

  return (
    <div className={`al-f ms-wrap${open ? ' is-open' : ''}`}>
      <div className="al-l">{label}</div>
      <button className="ms-btn" type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open}>
        <span style={{ color: value.length ? 'var(--text)' : 'var(--t3)' }}>{text}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open ? (
        <div className="ms-dd" role="listbox" aria-label={label}>
          {options.map((o) => {
            const checked = value.includes(o);
            return (
              <label key={o} className="ms-opt">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked ? [...value, o] : value.filter((x) => x !== o);
                    onChange(next);
                  }}
                />
                <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600 }}>{getLabel ? getLabel(o) : o}</span>
              </label>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default function AuditLogsPage() {
  const { t } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [activityTypes, setActivityTypes] = useState<string[]>([]);
  const [performedByUserIds, setPerformedByUserIds] = useState<string[]>([]);

  const [typeOptions, setTypeOptions] = useState<string[]>([]);
  const [actorOptions, setActorOptions] = useState<AuditLogUser[]>([]);

  const [rows, setRows] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [page, setPage] = useState(1);
  const [rpp, setRpp] = useState(5);

  useEffect(() => {
    void listAuditActivityTypes().then((r) => (r.success ? setTypeOptions(r.data) : null));
    void listAuditUsers().then((r) => (r.success ? setActorOptions(r.data) : null));
  }, []);

  const [filterOpen, setFilterOpen] = useState(true);
  const [pdfExporting, setPdfExporting] = useState(false);

  const [applied, setApplied] = useState({ from: '', to: '', activityTypes: [] as string[], performedByUserIds: [] as string[] });

  useEffect(() => {
    void listAuditLogs({ ...applied, page, rowsPerPage: rpp }).then((r) => {
      if (!r.success) return;
      setRows(r.data.rows);
      setTotal(r.data.total);
    });
  }, [applied, page, rpp]);

  const relativeTime = useCallback(
    (iso: string): string => {
      const diff = Date.now() - new Date(iso).getTime();
      const secs = Math.floor(diff / 1000);
      if (secs < 60) return t('audit.justNow');
      const mins = Math.floor(secs / 60);
      if (mins < 60) return t('audit.minutesAgo', { count: mins });
      const hours = Math.floor(mins / 60);
      if (hours < 24) return t('audit.hoursAgo', { count: hours });
      const days = Math.floor(hours / 24);
      if (days === 1) return t('audit.yesterday');
      return t('audit.daysAgo', { count: days });
    },
    [t],
  );

  const totalPages = Math.max(1, Math.ceil(total / rpp));
  const safePage = Math.min(page, totalPages);
  const start = total === 0 ? 0 : (safePage - 1) * rpp + 1;
  const end = Math.min(safePage * rpp, total);

  const pagerPages = useMemo(() => {
    if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (safePage === 1) return [1, 2, 3];
    if (safePage >= totalPages) return [totalPages - 2, totalPages - 1, totalPages];
    return [safePage - 1, safePage, safePage + 1];
  }, [totalPages, safePage]);

  const today = new Date().toISOString().slice(0, 10);

  const handleFromChange = (value: string) => {
    setFrom(value);
    if (value && to && value > to) {
      setTo('');
    }
  };

  return (
    <>
      <div className="bc">
        <span>{t('audit.breadcrumb')}</span>
      </div>

      <div className="al-hdr">
        <div>
          <div className="pg-title" style={{ marginBottom: 0 }}>
            {t('audit.title')}
          </div>
        </div>
        <div className="al-actions">
          <Button
            variant="primary"
            disabled={pdfExporting}
            onClick={() => {
              setPdfExporting(true);
              void exportAuditLogsPdf({
                from: applied.from || undefined,
                to: applied.to || undefined,
                activityTypes: applied.activityTypes.length ? applied.activityTypes : undefined,
                performedByUserIds: applied.performedByUserIds.length ? applied.performedByUserIds : undefined,
              })
                .then((r) => {
                  if (!r.success) {
                    setFlashToast({ message: r.message ?? t('reports.exportFailed'), variant: 'error' });
                  }
                })
                .finally(() => setPdfExporting(false));
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {pdfExporting ? t('audit.generating') : t('audit.exportPdf')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void exportAuditLogsCsv({
                from: applied.from || undefined,
                to: applied.to || undefined,
                activityTypes: applied.activityTypes.length ? applied.activityTypes : undefined,
                performedByUserIds: applied.performedByUserIds.length ? applied.performedByUserIds : undefined,
              });
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {t('audit.exportCsv')}
          </Button>
        </div>
      </div>

      <div className="al-filter">
        <div className="al-filter-top" role="button" tabIndex={0} onClick={() => setFilterOpen((v) => !v)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? setFilterOpen((v) => !v) : null)} style={{ marginBottom: filterOpen ? 12 : 0 }}>
          <div className="al-filter-title">{t('audit.filters')}</div>
          <div className="chevron" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points={filterOpen ? '6 15 12 9 18 15' : '6 9 12 15 18 9'} />
            </svg>
          </div>
        </div>

        {filterOpen ? (
          <>
            <div className="al-filter-grid">
              <div className="al-f">
                <div className="al-l">{t('audit.activityFrom')}</div>
                <FormattedDateInput
                  className="al-inp"
                  value={from}
                  onChange={handleFromChange}
                  enableYmdViews={false}
                  maxDate={to || today}
                />
              </div>
              <div className="al-f">
                <div className="al-l">To</div>
                <FormattedDateInput
                  className="al-inp"
                  value={to}
                  onChange={setTo}
                  enableYmdViews={false}
                  minDate={from || undefined}
                  maxDate={today}
                  disabled={!from}
                />
              </div>

              <MultiSelect
                label={t('audit.activityTypes')}
                value={activityTypes}
                options={typeOptions}
                placeholder={t('audit.selectActivityTypes')}
                onChange={(v) => setActivityTypes(v)}
                getLabel={formatActivityLabel}
              />

              <MultiSelect
                label={t('audit.activityPerformedBy')}
                value={performedByUserIds}
                options={actorOptions.map((u) => u.id)}
                placeholder={t('audit.selectActorNames')}
                onChange={setPerformedByUserIds}
                getLabel={(id) => actorOptions.find((u) => u.id === id)?.displayName ?? id}
              />
            </div>

            <div className="al-filter-actions">
              <Button
                onClick={() => {
                  setFrom('');
                  setTo('');
                  setActivityTypes([]);
                  setPerformedByUserIds([]);
                  setApplied({ from: '', to: '', activityTypes: [], performedByUserIds: [] });
                  setPage(1);
                  setExpanded({});
                }}
              >
                {t('common.reset')}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setApplied({ from, to, activityTypes, performedByUserIds });
                  setPage(1);
                  setExpanded({});
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                {t('common.search')}
              </Button>
            </div>
          </>
        ) : null}
      </div>

      <div className="al-list">
        {rows.length === 0 ? <div className="al-empty">{t('audit.noLogsFound')}</div> : null}
        {rows.map((r) => {
          const open = expanded[r.id] ?? false;
          return (
            <div key={r.id} className="al-item">
              <div
                className="al-row"
                role="button"
                tabIndex={0}
                onClick={() => setExpanded((m) => ({ ...m, [r.id]: !open }))}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? (e.preventDefault(), setExpanded((m) => ({ ...m, [r.id]: !open }))) : null)}
              >
                <div>
                  <div className="al-at">{formatAuditDateTime(r.at)}</div>
                  <div className="al-rel">{relativeTime(r.at)}</div>
                </div>
                <div className="al-title">{r.title}</div>
                <div className="al-actor">{r.performedBy}</div>
                <div className="al-chev" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points={open ? '6 15 12 9 18 15' : '6 9 12 15 18 9'} />
                  </svg>
                </div>
              </div>
              {open ? (
                <div className="al-body">
                  <div className="al-meta">
                    <div>
                      <h5>{t('audit.activityType')}</h5>
                      <ActivityTypePill type={r.activityType} />
                      <div style={{ height: 10 }} />
                      <h5>{t('audit.summary')}</h5>
                      <div className="al-sum">{r.summary || '-'}</div>
                    </div>
                    <div>
                      <h5>{t('audit.targetEntity')}</h5>
                      <div className="al-kv">
                        {r.targetEntityLabel || '-'}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}

        <div className="tft">
          <div className="tfi">{t('audit.showingRange', { start, end, total })}</div>
          <div className="pgr">
            <span className="rpl">Rows per page:</span>
            <select
              className="rpsel"
              value={rpp}
              onChange={(e) => {
                setRpp(Number(e.target.value));
                setPage(1);
              }}
            >
              {[5, 10, 20].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <div className="pgg">
              <button className={`pgb ${safePage <= 1 ? 'dis' : ''}`} type="button" onClick={() => (safePage <= 1 ? null : setPage(safePage - 1))}>
                {t('common.previous')}
              </button>
              {pagerPages.map((p) => (
                <button key={p} className={`pgb ${p === safePage ? 'on' : ''}`} type="button" onClick={() => setPage(p)}>
                  {p}
                </button>
              ))}
              <button className={`pgb ${safePage >= totalPages ? 'dis' : ''}`} type="button" onClick={() => (safePage >= totalPages ? null : setPage(safePage + 1))}>
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
